/* ----------------------------------------------------------------------------
<image-compare>: wipes between two shots of the same thing. The seam sits at
--position, the first figure fills the space to its left and the second shows
through to its right, so raising the value reveals more of the first.

    <image-compare label="Compare the light and dark themes">
      <figure><img alt="…"><figcaption>Light</figcaption></figure>
      <figure><img alt="…"><figcaption>Dark</figcaption></figure>
    </image-compare>

The control is a real <input type="range"> stretched over the image and left
transparent, so dragging, tapping the track and the arrow/Home/End keys all come
from the browser rather than from pointer handlers here. Two details earn their
keep: step="any" keeps dragging continuous while arrow keys still move by 1, and
the thumb is zero-width because a thumb with width stops half its own size short
of either end, drifting from the seam the CSS draws at --position.

Without JavaScript the CSS still splits the pair down the middle, captioned, so
both shots are visible — there is just nothing to drag.
---------------------------------------------------------------------------- */

export default class ImageCompare extends HTMLElement {
	connectedCallback() {
		const figures = this.querySelectorAll(':scope > figure');
		if (figures.length !== 2) return; // not a pair: leave the markup alone

		this.names = [...figures].map(
			(figure, i) =>
				figure.querySelector('figcaption')?.textContent.trim() ||
				`image ${i + 1}`,
		);

		this.slider = document.createElement('input');
		this.slider.type = 'range';
		this.slider.className = 'image-compare__slider';
		this.slider.min = 0;
		this.slider.max = 100;
		this.slider.step = 'any';
		this.slider.value = this.getAttribute('position') ?? 50;
		this.slider.setAttribute(
			'aria-label',
			this.getAttribute('label') || `Compare ${this.names.join(' and ')}`,
		);
		this.slider.addEventListener('input', () => this.update());

		// aria-hidden: the range input over it is the control, and a second stop
		// on the same thing is noise
		this.handle = document.createElement('span');
		this.handle.className = 'image-compare__handle';
		this.handle.setAttribute('aria-hidden', 'true');

		this.append(this.handle, this.slider);
		this.update();
	}

	update() {
		const shown = Math.round(this.slider.value);
		this.style.setProperty('--position', `${this.slider.value}%`);
		// the bare percentage a range announces says nothing about which way round
		// the pair is, so spell out how much of each is on screen
		this.slider.setAttribute(
			'aria-valuetext',
			`${shown}% ${this.names[0]}, ${100 - shown}% ${this.names[1]}`,
		);
	}
}

window.customElements.define('image-compare', ImageCompare);
